import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_HTML = `<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif; color:#334155; font-size:13px; line-height:1.5;">
  <tr>
    <td valign="top" style="padding-right:16px;">
      <img src="{LogoUrl}" alt="{CompanyName}" width="92" referrerpolicy="no-referrer" style="display:block; border:0; outline:none;" />
    </td>
    <td valign="top" style="border-left:3px solid #ea580c; padding-left:16px;">
      <div style="font-size:16px; font-weight:bold; color:#0f172a;">{DisplayName}</div>
      <div style="color:#475569; margin-top:2px;">{JobTitle}</div>
      <div style="color:#64748b; margin-top:2px;">{Department}</div>
      <div style="margin-top:8px; font-size:12px;">
        <a href="mailto:{Mail}" style="color:#ea580c; text-decoration:none;">{Mail}</a>
        <span style="color:#cbd5e1;">&nbsp;|&nbsp;</span>
        <span style="color:#334155;">{BusinessPhone}</span>
        <span style="color:#cbd5e1;">&nbsp;|&nbsp;</span>
        <span style="color:#334155;">Ext: {Extension}</span>
      </div>
      <div style="margin-top:10px; padding-top:8px; border-top:1px solid #e2e8f0; font-style:italic; color:#ea580c; font-size:12px;">
        {Slogan}
      </div>
      <div style="margin-top:4px; font-size:11px; color:#64748b;">{Address}</div>
      <div style="margin-top:2px; font-size:11px;">
        <a href="{Website}" style="color:#ea580c; text-decoration:none;">{Website}</a>
      </div>
    </td>
  </tr>
</table>`;

const DEFAULT_BRANDING = {
  logo_url: "https://hospitalmaria.org/firma_outlook365/hm-logo.png",
  slogan: "Cambiamos la Vida de Nuestros Pacientitos",
  address: "Av. Institucional 123, Ciudad",
  website: "https://hospital.example.com",
  company_name: "Hospital Pediátrico",
};

const toEditorState = (data) => ({
  name: data.name, category: data.category,
  description: data.description || "",
  html_content: data.html_content, css_content: data.css_content || "",
  is_active: data.is_active,
  image_strategy: data.image_strategy || "external",
});

/**
 * Central state + persistence for the Template Editor page.
 * Loads variables/users/settings/images/template on mount; exposes small
 * action helpers (save, load versions, restore, sendTestMail, insert*).
 */
export function useTemplateEditor(id, isNew) {
  const navigate = useNavigate();

  const [template, setTemplate] = useState({
    name: "", category: "Hospital", description: "",
    html_content: DEFAULT_HTML, css_content: "", is_active: true,
    image_strategy: "external",
  });
  const [variables, setVariables] = useState([]);
  const [users, setUsers] = useState([]);
  const [images, setImages] = useState([]);
  const [previewUserId, setPreviewUserId] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loaded, setLoaded] = useState(isNew);

  // ---- initial load: variables/users/settings/images + optional template ----
  useEffect(() => {
    let cancelled = false;
    const loadInitial = async () => {
      const [vr, ur, sr, ir] = await Promise.all([
        api.get("/templates/variables"), api.get("/users"), api.get("/settings"),
        api.get("/images"),
      ]);
      if (cancelled) return;
      setVariables(vr.data);
      setUsers(ur.data);
      setImages(ir.data);
      setPreviewUserId((prev) => prev || (ur.data.length ? ur.data[0].id : ""));
      const liveBranding = sr.data?.branding || {};
      setBranding((prev) => ({ ...prev, ...liveBranding }));
      if (!isNew) {
        const { data } = await api.get(`/templates/${id}`);
        if (cancelled) return;
        setTemplate(toEditorState(data));
        setLoaded(true);
      }
    };
    loadInitial();
    return () => { cancelled = true; };
  }, [id, isNew]);

  // ---- live preview: substitute {Vars} with the selected user / branding ----
  const refreshPreview = useCallback(() => {
    // Always render the CURRENT html_content locally so unsaved edits are visible.
    const selectedUser = users.find((u) => u.id === previewUserId);
    const bp = selectedUser?.business_phones?.[0] || "";
    const ext = selectedUser?.extension_attributes?.extensionAttribute1 || "";
    const ctx = {
      DisplayName: selectedUser?.display_name || "Dra. María González",
      FirstName: selectedUser?.given_name || "María",
      LastName: selectedUser?.surname || "González",
      JobTitle: selectedUser?.job_title || "Jefa de Pediatría",
      Department: selectedUser?.department || "Pediatría",
      Mail: selectedUser?.mail || selectedUser?.user_principal_name || "maria.gonzalez@hospital.example.com",
      BusinessPhone: bp || "+57 601 555-0100",
      MobilePhone: selectedUser?.mobile_phone || "",
      Extension: ext || "1234",
      Office: selectedUser?.office_location || "",
      CompanyName: branding.company_name || selectedUser?.company_name || "Hospital Pediátrico",
      Manager: selectedUser?.manager || "",
      EmployeeID: selectedUser?.employee_id || "",
      Photo: selectedUser?.photo_url || "",
      LogoUrl: branding.logo_url || DEFAULT_BRANDING.logo_url,
      Slogan: branding.slogan || DEFAULT_BRANDING.slogan,
      Address: branding.address || DEFAULT_BRANDING.address,
      Website: branding.website || DEFAULT_BRANDING.website,
    };
    const rendered = (template.html_content || "").replace(
      /\{([A-Za-z0-9_]+)\}/g,
      (_, key) => (ctx[key] !== undefined ? ctx[key] : "")
    );
    setPreviewHtml(rendered);
  }, [template.html_content, previewUserId, users, branding]);

  useEffect(() => {
    const t = setTimeout(refreshPreview, 200);
    return () => clearTimeout(t);
  }, [refreshPreview]);

  const insertVariable = useCallback((key) => {
    setTemplate((prev) => ({ ...prev, html_content: prev.html_content + `{${key}}` }));
  }, []);

  const insertImage = useCallback((img, options = { width: "120" }) => {
    // Build an email-safe <img> tag: inline styles, referrerpolicy, no JS.
    const width = options.width && /^\d+$/.test(options.width)
      ? ` width="${options.width}"` : "";
    const altText = (img.alt_text || img.name || "").replace(/"/g, "&quot;");
    const tag = `<img src="${img.url}" alt="${altText}"${width} referrerpolicy="no-referrer" style="display:block; border:0; outline:none; max-width:100%;" />`;
    setTemplate((prev) => ({
      ...prev,
      html_content: (prev.html_content || "") + "\n" + tag,
    }));
    toast.success(`Imagen "${img.name}" insertada`);
  }, []);

  const doSave = useCallback(async () => {
    if (!template.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/templates", template);
        toast.success("Plantilla creada");
        navigate(`/templates/${data.id}`);
      } else {
        await api.put(`/templates/${id}`, { ...template, change_note: changeNote || undefined });
        toast.success("Cambios guardados. Nueva versión creada.");
        setChangeNote("");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  }, [template, isNew, id, changeNote, navigate]);

  const loadVersions = useCallback(async () => {
    if (isNew) return [];
    const { data } = await api.get(`/templates/${id}/versions`);
    setVersions(data);
    return data;
  }, [isNew, id]);

  const restoreVersion = useCallback(async (v) => {
    try {
      await api.post(`/templates/${id}/restore/${v}`);
      toast.success(`Versión ${v} restaurada`);
      const { data } = await api.get(`/templates/${id}`);
      setTemplate(toEditorState(data));
      await loadVersions();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al restaurar");
      return false;
    }
  }, [id, loadVersions]);

  const sendTestMail = useCallback(async (toEmail) => {
    if (!previewUserId || !toEmail) {
      toast.error("Seleccione usuario y correo destino");
      return false;
    }
    try {
      const { data } = await api.post("/signatures/test-mail", {
        template_id: id, user_id: previewUserId, to_email: toEmail,
      });
      const cidNote = data.image_strategy === "cid"
        ? ` · ${data.inline_images} imagen(es) embebidas (CID)` : "";
      toast.success(`${data.message}${cidNote}`);
      const failed = data.cid_failures?.length || 0;
      if (failed) toast.warning(`${failed} imagen(es) no se pudieron embeber y se enviaron como URL externa`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando prueba");
      return false;
    }
  }, [id, previewUserId]);

  return {
    template, setTemplate,
    variables, users, images,
    previewUserId, setPreviewUserId, previewHtml,
    changeNote, setChangeNote,
    versions,
    saving, loaded,
    doSave, loadVersions, restoreVersion, sendTestMail,
    insertVariable, insertImage,
  };
}
